import { TicketLogModel } from "@jugl-web/rest-api/tickets/models/TicketLog";
import {
  TicketCreatedLogInfo,
  TicketUpdatedLogInfo,
} from "@jugl-web/rest-api/tickets/models/TicketLog/TicketLogInfo";
import { isoToLocalDate } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { useCallback, useMemo } from "react";
import { ParsedTicketActivity } from "../types";
import { formatTicketUserName } from "../../../utils/formatTicketUserName";
import { useTicketActivityMessageRenderers } from "./useTicketActivityMessageRenderers";

export const useTicketActivityParser = () => {
  const renderers = useTicketActivityMessageRenderers();
  const { localeAwareFormat } = useFormattedDate();

  const formatDueDateLabel = useCallback(
    (dueAt: string) => localeAwareFormat(isoToLocalDate(dueAt), "MMM d, yyyy"),
    [localeAwareFormat]
  );

  const isCreatedLogInfo = useCallback(
    (
      log: TicketLogModel
    ): log is TicketLogModel & { info: TicketCreatedLogInfo } =>
      log.action === "created",
    []
  );

  const isUpdatedLogInfo = useCallback(
    (
      log: TicketLogModel
    ): log is TicketLogModel & { info: TicketUpdatedLogInfo } =>
      log.action === "updated",
    []
  );

  const parseUpdatedLog = useCallback(
    (
      log: TicketLogModel & { info: TicketUpdatedLogInfo },
      actorName: string
    ): ParsedTicketActivity[] => {
      const { info } = log;
      const date = new Date(isoToLocalDate(log.inserted_at));
      const activities: ParsedTicketActivity[] = [];

      const pushActivity = (
        suffix: string,
        activity: Omit<ParsedTicketActivity, "id" | "date" | "raw">
      ) => {
        activities.push({
          ...activity,
          id: `${log.id}:${suffix}`,
          date,
          raw: log,
        });
      };

      if (info.contact) {
        pushActivity("contact", {
          message: renderers.customerUpdatedMessageRenderer(
            info.contact.name,
            actorName
          ),
          icon: { kind: "customer" },
        });
      }

      if (info.assignees_added?.length) {
        pushActivity("assignees-added", {
          message: renderers.assigneesAddedMessageRenderer(
            info.assignees_added,
            actorName
          ),
          icon: { kind: "assignees" },
        });
      }

      if (info.assignees_removed?.length) {
        pushActivity("assignees-removed", {
          message: renderers.assigneesRemovedMessageRenderer(
            info.assignees_removed,
            actorName
          ),
          icon: { kind: "assignees" },
        });
      }

      if (info.due_at !== undefined) {
        pushActivity("due-at", {
          message: renderers.dueDateSetMessageRenderer(
            info.due_at ? <b>{formatDueDateLabel(info.due_at)}</b> : <b>—</b>,
            actorName
          ),
          icon: { kind: "due_date" },
        });
      }

      if (info.priority !== undefined) {
        pushActivity("priority", {
          message: renderers.prioritySetMessageRenderer(
            <b>{renderers.getPriorityLabel(info.priority)}</b>,
            actorName
          ),
          icon: { kind: "priority", priority: info.priority },
        });
      }

      if (info.status !== undefined) {
        pushActivity("status", {
          message: renderers.statusSetMessageRenderer(
            <b>{renderers.getStatusLabel(info.status)}</b>,
            actorName
          ),
          icon: { kind: "status", status: info.status },
        });
      }

      if (activities.length === 0) {
        return [
          {
            id: log.id,
            message: renderers.fallbackUpdatedMessageRenderer(actorName),
            date,
            icon: { kind: "created" },
            raw: log,
          },
        ];
      }

      return activities;
    },
    [formatDueDateLabel, renderers]
  );

  const parseTicketLog = useCallback(
    (log: TicketLogModel): ParsedTicketActivity[] => {
      const actorName = formatTicketUserName(log.action_by);
      const date = new Date(isoToLocalDate(log.inserted_at));

      if (isCreatedLogInfo(log)) {
        return [
          {
            id: log.id,
            message: renderers.ticketCreatedMessageRenderer(
              log.info.contact.name,
              actorName
            ),
            date,
            icon: { kind: "created" },
            raw: log,
          },
        ];
      }

      if (isUpdatedLogInfo(log)) {
        return parseUpdatedLog(log, actorName);
      }

      return [];
    },
    [isCreatedLogInfo, isUpdatedLogInfo, parseUpdatedLog, renderers]
  );

  const parseTicketLogs = useCallback(
    (logs: TicketLogModel[]) => logs.flatMap(parseTicketLog),
    [parseTicketLog]
  );

  return useMemo(
    () => ({ parseTicketLog, parseTicketLogs }),
    [parseTicketLog, parseTicketLogs]
  );
};
